import { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner, Table, UncontrolledTooltip } from 'reactstrap';
import { Eye, Edit, Play, Trash2, Database, Layers, Plus, FileText } from 'react-feather';
import { AiOutlineRobot } from 'react-icons/ai';
import { RiStopCircleLine } from 'react-icons/ri';
import Swal from 'sweetalert2';
import Select from 'react-select';
import {
  BotManagerAuditLog,
  BotManagerTask,
  deleteRpaTask,
  fetchBotManagerUsers,
  fetchRpaAuditLog,
  fetchRpaTasks,
  fetchRpaErrorImage,
  fetchUserPermissions,
  startRpaTask,
  startRpaTaskRuntime,
  stopRpaTask,
  stopRpaTaskRuntime,
  updateRpaTask,
  fetchClients,
  fetchTemplates,
  fetchTemplateCodes,
} from './store';
import WsServicePath from '../../../Service/websocket_webservice';
import ColumnChooser, { ColumnOption, isColVisible, visibleColCount } from '../../../Components/ColumnChooser/ColumnChooser';
import { useColumnPreferences } from '../../../hooks/useColumnPreferences';

const safeDisplay = (value: unknown) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  return String(value);
};

const renderAuditValue = (value: any, fieldName?: string) => {
  if (value === null || value === undefined || value === '') return '—';

  const isStepsField = fieldName && fieldName.toLowerCase().trim() === 'steps';

  if (isStepsField) {
    let stepsArray: any[] = [];
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        stepsArray = normalizeStepsToArray(parsed);
      } catch (e) {
        // Not a JSON string
      }
    } else {
      stepsArray = normalizeStepsToArray(value);
    }

    if (stepsArray && stepsArray.length > 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', textAlign: 'left' }}>
          {stepsArray.map((step: any, index: number) => {
            const stepNum = step?.step || (index + 1);
            const promptVal = step?.prompt || step?.description || '';
            return (
              <div key={index} style={{ fontSize: '11px', lineHeight: '1.4' }}>
                <span className="fw-bold" style={{ color: '#4f46e5' }}>Step {stepNum}:</span> {promptVal}
              </div>
            );
          })}
        </div>
      );
    }
  }

  const isDatasetField = fieldName && fieldName.toLowerCase().trim() === 'dataset';

  if (isDatasetField) {
    let datasetRows: any[] = [];

    const extractRows = (val: any): any[] => {
      if (val === null || val === undefined) return [];

      if (typeof val === 'string') {
        const trimmed = val.trim();
        if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
          try {
            const parsed = JSON.parse(trimmed);
            return extractRows(parsed);
          } catch (e) {
            try {
              const formatted = trimmed
                .replace(/'/g, '"')
                .replace(/None/g, 'null')
                .replace(/True/g, 'true')
                .replace(/False/g, 'false');
              const parsed = JSON.parse(formatted);
              return extractRows(parsed);
            } catch (e2) {
              // Not a valid JSON string even after replacement
            }
          }
        }
        return [];
      }

      if (Array.isArray(val)) {
        return val.flatMap(item => {
          if (item && typeof item === 'object') {
            return [item];
          }
          if (typeof item === 'string') {
            try {
              const parsed = JSON.parse(item);
              return typeof parsed === 'object' && parsed !== null ? [parsed] : [];
            } catch (e) {
              return [];
            }
          }
          return [];
        });
      }

      if (typeof val === 'object') {
        return [val];
      }

      return [];
    };

    datasetRows = extractRows(value);

    if (datasetRows && datasetRows.length > 0) {
      const allKeys = Array.from(
        new Set(datasetRows.flatMap((row) => (row && typeof row === 'object' ? Object.keys(row) : [])))
      );

      if (allKeys.length > 0) {
        return (
          <div className="table-responsive" style={{ maxHeight: '200px', overflowY: 'auto', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
            <table className="table table-sm table-bordered mb-0" style={{ fontSize: '11px', minWidth: '350px' }}>
              <thead style={{ backgroundColor: '#f8fafc', position: 'sticky', top: 0 }}>
                <tr>
                  {allKeys.map((key) => (
                    <th key={key} className="px-2 py-1 small text-capitalize" style={{ fontWeight: 600, color: '#334155', borderBottom: '2px solid #e2e8f0' }}>
                      {key.replace(/_/g, ' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {datasetRows.map((row, idx) => (
                  <tr key={idx}>
                    {allKeys.map((key) => {
                      const cellVal = row && typeof row === 'object' ? row[key] : '';
                      return (
                        <td key={key} className="px-2 py-1 text-break" style={{ textAlign: 'left' }}>
                          {cellVal === null || cellVal === undefined || cellVal === '' ? '—' : String(cellVal)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
    }
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (e) {
      return '[Complex Object]';
    }
  }
  return String(value);
};

const normalizeStepsToArray = (steps: unknown): any[] => {
  if (Array.isArray(steps)) return steps;
  if (steps && typeof steps === 'object') return Object.values(steps as Record<string, any>);
  return [];
};

type LoopStepItem = {
  step: string;
  value: string;
};

type LoopStepsMap = Record<string, LoopStepItem[]>;

const stepKeyFromNumber = (stepNum: number) => `step${stepNum}`;

const stepNumberFromKey = (stepKey: string) => {
  const match = String(stepKey).match(/\d+/);
  return match ? Number(match[0]) : null;
};

const getLoopNumber = (loopKey: string) => {
  const match = String(loopKey).match(/loop[_\s-]*(\d+)/i);
  return match ? Number(match[1]) : null;
};

const normalizeLoopStepValue = (value: string) => {
  if (value === 'Procedure code') return 'procedure_codes';
  if (value === 'History usage code') return 'history_usages';
  return value;
};

const createLoopStepsMap = (loopCount: number): LoopStepsMap => (
  Array.from({ length: Math.max(1, loopCount) }).reduce<LoopStepsMap>((acc, _, index) => {
    acc[`loop_${index + 1}`] = [];
    return acc;
  }, {})
);

const normalizeLoopStepItem = (step: unknown): LoopStepItem | null => {
  if (typeof step === 'number') {
    return { step: stepKeyFromNumber(step), value: '' };
  }

  if (typeof step === 'string') {
    return {
      step: /^step\d+$/i.test(step) ? step.toLowerCase() : step,
      value: '',
    };
  }

  if (step && typeof step === 'object') {
    const item = step as Record<string, unknown>;
    const stepValue = item.step ?? item.Step ?? item.name;
    if (!stepValue) return null;

    return {
      step: typeof stepValue === 'number'
        ? stepKeyFromNumber(stepValue)
        : String(stepValue),
      value: normalizeLoopStepValue(String(item.value ?? item.Value ?? '')),
    };
  }

  return null;
};

const normalizeLoopSteps = (loopSteps: unknown): LoopStepsMap => {
  if (Array.isArray(loopSteps)) {
    return loopSteps.reduce<LoopStepsMap>((acc, stepNum) => {
      const item = normalizeLoopStepItem(stepNum);
      if (item) acc.loop_1.push(item);
      return acc;
    }, { loop_1: [] });
  }

  if (loopSteps && typeof loopSteps === 'object') {
    const entries = Object.entries(loopSteps as Record<string, unknown>);
    if (entries.length === 0) return { loop_1: [] };

    return entries.reduce<LoopStepsMap>((acc, [loopKey, steps]) => {
      const normalizedLoopKey = /^loop_\d+$/i.test(loopKey)
        ? loopKey.toLowerCase()
        : `loop_${Object.keys(acc).length + 1}`;
      acc[normalizedLoopKey] = Array.isArray(steps)
        ? steps.reduce<LoopStepItem[]>((items, step) => {
          const item = normalizeLoopStepItem(step);
          if (item) items.push(item);
          return items;
        }, [])
        : [];
      return acc;
    }, {});
  }

  return { loop_1: [] };
};

const getLoopCountFromSteps = (steps: any[]) => {
  return steps.reduce((maxLoop, step) => {
    const valuesToCheck = [
      step?.loop,
      step?.loop_id,
      step?.loopId,
      step?.loop_name,
      step?.loopName,
      step?.prompt,
      step?.description,
      step?.action,
    ];

    valuesToCheck.forEach((value) => {
      const loopNumber = getLoopNumber(String(value || ''));
      if (loopNumber && loopNumber > maxLoop) {
        maxLoop = loopNumber;
      }
    });

    return maxLoop;
  }, 0);
};

const getLoopCountFromLoopSteps = (loopSteps: LoopStepsMap) => {
  return Object.keys(loopSteps).reduce((maxLoop, loopKey) => {
    const loopNumber = getLoopNumber(loopKey);
    return loopNumber && loopNumber > maxLoop ? loopNumber : maxLoop;
  }, 0);
};

const extractPlaceholders = (prompt: string): string[] => {
  const regex = /\{+([^{}]+?)\}+/g;
  const matches: string[] = [];
  let match: RegExpExecArray | null;
  regex.lastIndex = 0;
  while ((match = regex.exec(prompt)) !== null) {
    if (match[1]) {
      matches.push(match[1].trim());
    }
  }
  return matches;
};

const extractDatasetHeaders = (steps: any[] | Record<string, any> = []) => {
  const regex = /\{+([^{}]+?)\}+/g;
  const nameCount = new Map<string, number>();
  const headers: Array<{ key: string; prompt: string; description: string }> = [];

  const stepsArray = Array.isArray(steps)
    ? steps
    : (steps && typeof steps === 'object'
      ? Object.values(steps)
      : []);

  stepsArray.forEach((step) => {
    const prompt = step?.prompt || step?.description || '';
    const description = step?.Detailed_description || step?.detailed_description || step?.description || step?.prompt || '';
    if (!prompt) return;
    const matches = prompt.matchAll(regex);
    for (const match of matches) {
      const headerKey = match[1]?.trim();
      if (!headerKey) continue;
      const count = nameCount.get(headerKey) || 0;
      nameCount.set(headerKey, count + 1);
      headers.push({
        key: count === 0 ? headerKey : `${headerKey}_${count + 1}`,
        prompt,
        description,
      });
    }
  });

  return headers;
};

const normalizeDatasetRows = (dataset: Record<string, any> | any[] | undefined, headers: Array<{ key: string; prompt: string; description: string }>) => {
  if (Array.isArray(dataset)) {
    return dataset.length > 0 ? dataset : headers.length > 0 ? [Object.fromEntries(headers.map((header) => [header.key, '']))] : [];
  }

  if (dataset && typeof dataset === 'object') {
    const keys = Object.keys(dataset);
    if (keys.length > 0) {
      const firstKey = keys[0];
      const rowCount = Array.isArray((dataset as any)[firstKey]) ? (dataset as any)[firstKey].length : 0;
      const rows = [];
      for (let index = 0; index < rowCount; index += 1) {
        const row: Record<string, any> = {};
        keys.forEach((key) => {
          const values = (dataset as any)[key];
          row[key] = Array.isArray(values) ? values[index] || '' : '';
        });
        rows.push(row);
      }
      return rows;
    }
  }

  return headers.length > 0 ? [Object.fromEntries(headers.map((header) => [header.key, '']))] : [];
};

const loopColors = [
  { text: '#15803d', bg: '#dcfce7', emoji: '🟢' }, // Loop 1
  { text: '#1d4ed8', bg: '#dbeafe', emoji: '🔵' }, // Loop 2
  { text: '#7e22ce', bg: '#f3e8ff', emoji: '🟣' }, // Loop 3
  { text: '#c2410c', bg: '#ffedd5', emoji: '🟠' }, // Loop 4
  { text: '#a16207', bg: '#fef9c3', emoji: '🟡' }, // Loop 5
  { text: '#b91c1c', bg: '#fee2e2', emoji: '🔴' }, // Loop 6
  { text: '#0e7490', bg: '#ecfeff', emoji: '🟤' }, // Loop 7
];

const numberDuplicatePlaceholders = (steps: any[]): any[] => {
  const placeholderCount = new Map<string, number>();
  const regex = /\{+([^{}]+?)\}+/g;

  return steps.map((step) => {
    const prompt = step.prompt || '';
    let newPrompt = prompt;
    const matches: Array<{ match: string; placeholder: string; index: number }> = [];
    let match: RegExpExecArray | null;

    regex.lastIndex = 0;

    while ((match = regex.exec(prompt)) !== null) {
      matches.push({
        match: match[0],
        placeholder: match[1].trim(),
        index: match.index,
      });
    }

    for (let i = matches.length - 1; i >= 0; i--) {
      const { match: originalMatch, placeholder, index } = matches[i];
      const count = placeholderCount.get(placeholder) || 0;
      placeholderCount.set(placeholder, count + 1);

      let replacement: string;
      if (count === 0) {
        replacement = `{${placeholder}}`;
      } else {
        replacement = `{${placeholder}_${count + 1}}`;
      }

      newPrompt = newPrompt.slice(0, index) + replacement + newPrompt.slice(index + originalMatch.length);
    }

    return {
      ...step,
      prompt: newPrompt,
    };
  });
};

interface RpabotManagerProps {
  isActive?: boolean;
  setActiveTab?: (tab: 'tasks' | 'rpa' | 'assistant' | 'rpa-assistant') => void;
}

const BOT_STATUS_FILTER_OPTIONS = [
  { value: 'Y', label: 'Yet to Start' },
  { value: 'I', label: 'In Progress' },
  { value: 'S', label: 'Stopped' },
  { value: 'E', label: 'Error' },
];

const RpabotManager = ({ isActive = true, setActiveTab }: RpabotManagerProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tasks, setTasks] = useState<BotManagerTask[]>([]);
  const [users, setUsers] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sort, setSort] = useState<'asc' | 'desc'>('desc');
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [filterUserId, setFilterUserId] = useState('');
  const [filterBotStatus, setFilterBotStatus] = useState('');
  const [total, setTotal] = useState(0);
  const [selectedTask, setSelectedTask] = useState<BotManagerTask | null>(null);
  const [auditLogs, setAuditLogs] = useState<BotManagerAuditLog[]>([]);
  const [auditOpen, setAuditOpen] = useState(false);
  const [instructionOpen, setInstructionOpen] = useState(false);
  const [instructionMode, setInstructionMode] = useState<'edit' | 'start'>('edit');
  const [datasetRows, setDatasetRows] = useState<Array<Record<string, any>>>([]);
  const [instructionText, setInstructionText] = useState('');
  const [instructionTitle, setInstructionTitle] = useState('');
  const [instructionDomain, setInstructionDomain] = useState('');
  const [instructionDescription, setInstructionDescription] = useState('');
  const [instructionSteps, setInstructionSteps] = useState<Array<{
    step: number;
    prompt: string;
    action: string;
    tag: string;
    id: string;
    class: string;
    xpath: string;
    input_type: string;
    placeholder: string;
  }>>([]);
  const [loopSteps, setLoopSteps] = useState<LoopStepsMap>({ loop_1: [] });
  const [loopCount, setLoopCount] = useState(1);
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});
  const [pendingSelectedValues, setPendingSelectedValues] = useState<Record<string, string>>({});
  const [multipleCheckedSteps, setMultipleCheckedSteps] = useState<Record<string, boolean>>({});
  const [runningTaskId, setRunningTaskId] = useState<string | null>(null);
  const [clientOptions, setClientOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [templateOptions, setTemplateOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templateCode, setTemplateCode] = useState<any[]>([]);
  const [templateHistoryCode, setTemplateHistoryCode] = useState<any[]>([]);
  const [menuActions, setMenuActions] = useState<string>('');
  const [errorDetails, setErrorDetails] = useState<any>(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorLoading, setErrorLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const userId = useMemo(() => localStorage.getItem('id') || '', []);
  const username = useMemo(() => localStorage.getItem('UserName') || '', []);
  const userType = useMemo(() => localStorage.getItem('UserType') || '', []);

  const { savedColumns, saveColumns } = useColumnPreferences('RpaBotManagerListing');
  const columnOptions: ColumnOption[] = [
    { id: 'BotName', label: 'Bot Name', locked: true },
    { id: 'Description', label: 'Description' },
    { id: 'Username', label: 'Username' },
    { id: 'BotStatus', label: 'Bot Status' },
    { id: 'Actions', label: 'Actions', locked: true },
  ];
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const dropdownOptions = [
    { label: "Select Input Data", value: "select_input_data" },
    { label: "Procedure code", value: "procedure_codes" },
    { label: "History usage code", value: "history_usages" },
  ];


  // console.log('templateCode', templateCode);
  // console.log('templateHistoryCode', templateHistoryCode);


  const loadTasks = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const result = await fetchRpaTasks({ q: search, page, perPage, sort, sortColumn, filterUserId, filterBotStatus });
      setTasks(result.data);
      setTotal(result.count);
      setHasLoaded(true);
    } catch (error: any) {
      Swal.fire('Error', error.message || 'Failed to load bot manager data', 'error');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, [page, perPage, sort, sortColumn, filterUserId, filterBotStatus]);

  useEffect(() => {
    if (isActive) {
      setRunningTaskId(null);
      loadTasks(false);
    }
  }, [isActive]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      loadTasks();
    }, 300);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    fetchBotManagerUsers().then(setUsers).catch(() => undefined);
  }, [userType]);

  useEffect(() => {
    if (userId) {
      fetchUserPermissions(userId).then((response) => {
        if (Array.isArray(response) && response[0]?.Permission) {
          const action = response[0].Permission.find(
            (item: any) => item.MenuKey === 'BOTMANAGER'
          );
          if (action) {
            setMenuActions(action.MenuActions);
          }
        }
      }).catch(err => console.error('Failed to fetch permissions', err));
    }
  }, [userId]);

  useEffect(() => {
    fetchClients()
      .then((res) => {
        const mapped = (res || []).map((c) => ({
          label: c.CompanyName,
          value: c.id,
        }));
        setClientOptions(mapped);
      })
      .catch((err) => console.error('Failed to fetch clients:', err));
  }, []);

  useEffect(() => {
    const wsUrl = `${WsServicePath}ws/recorded_actions/`;
    const ws = new WebSocket(wsUrl);

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data) {
          const updatedDoc = data.fullDocument;
          const opType = data.operationType;
          const docId = updatedDoc?._id?.$oid || data.documentKey?._id?.$oid || (typeof data.documentKey?._id === 'string' ? data.documentKey?._id : null);

          if (docId) {
            if (opType === 'delete' || (updatedDoc && updatedDoc.status === 'R') || (updatedDoc && updatedDoc.RPA_status !== 'C')) {
              setTasks((prevTasks) => {
                const filtered = prevTasks.filter((t) => t.id !== docId);
                if (filtered.length !== prevTasks.length) {
                  setTotal((prevTotal) => Math.max(0, prevTotal - 1));
                }
                return filtered;
              });
            } else if (updatedDoc && (opType === 'update' || opType === 'replace')) {
              if (updatedDoc.bot_status && updatedDoc.bot_status !== 'I') {
                setRunningTaskId((prev) => (prev === docId ? null : prev));
              }
              setTasks((prevTasks) =>
                prevTasks.map((t) => {
                  if (t.id === docId) {
                    return {
                      ...t,
                      project_title: updatedDoc.name ?? t.project_title,
                      Description: updatedDoc.Description ?? t.Description,
                      bot_status: updatedDoc.bot_status ?? t.bot_status,
                      Domain: updatedDoc.initial_url ?? t.Domain,
                      steps: updatedDoc.steps ?? t.steps,
                      RPA_status: updatedDoc.RPA_status ?? t.RPA_status,
                      Dataset: updatedDoc.Dataset ?? t.Dataset,
                      Task_Type: updatedDoc.task_type ?? t.Task_Type,
                      credentialsReusable: updatedDoc.credentialsReusable ?? t.credentialsReusable,
                      Flow_error_details: updatedDoc.Flow_error_details ?? t.Flow_error_details,
                    };
                  }
                  return t;
                })
              );
            } else {
              loadTasks();
            }
          } else {
            loadTasks();
          }
        }
      } catch (error) {
        console.error('[RpabotManager] WebSocket message error:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('[RpabotManager] WebSocket error:', error);
    };

    return () => {
      ws.close();
    };
  }, []);

  const handleFlowError = async (task: BotManagerTask) => {
    setErrorDetails(null);
    setErrorLoading(true);
    setErrorModalOpen(true);
    try {
      const response = await fetchRpaErrorImage(task.id);
      const errorData = Array.isArray(response) ? response[0] : response;
      if (errorData) {
        setErrorDetails(errorData);
      } else {
        setErrorModalOpen(false);
        Swal.fire('Info', 'No error details found for this task.', 'info');
      }
    } catch (error: any) {
      setErrorModalOpen(false);
      Swal.fire('Error', error.message || 'Failed to fetch error details', 'error');
    } finally {
      setErrorLoading(false);
    }
  };

  const handleAudit = async (task: BotManagerTask) => {
    setSelectedTask(task);
    setAuditOpen(true);
    setAuditLogs([]);
    try {
      const data = await fetchRpaAuditLog(task.id);
      setAuditLogs(data);
    } catch (error: any) {
      Swal.fire('Error', error.message || 'Failed to fetch audit log', 'error');
      setAuditOpen(false);
    }
  };

  const handleDelete = async (task: BotManagerTask) => {
    const result = await Swal.fire({
      title: 'Delete assistant?',
      text: `You are about to delete "${task.project_title}".`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;

    try {
      await deleteRpaTask({ id: task.id, name: task.project_title, deletedBy: username });
      await loadTasks();
      Swal.fire('Success', 'Assistant deleted successfully', 'success');
    } catch (error: any) {
      Swal.fire('Error', error.message || 'Failed to delete assistant', 'error');
    }
  };

  const handleStartStop = async (task: BotManagerTask, action: 'start' | 'stop') => {
    try {
      if (action === 'start') {
        if ((window as any).taskMessagesCache) {
          (window as any).taskMessagesCache.delete(task.id);
        }
        setTasks((prevTasks) =>
          prevTasks.map((t) =>
            t.id === task.id ? { ...t, bot_status: 'I' } : t
          )
        );
        setRunningTaskId(task.id);
        await startRpaTask({ userId, username, rpaId: task.id, title: task.project_title });
        if (setActiveTab) {
          setActiveTab('rpa-assistant');
        }
        navigate('/bot-manager', { state: { activeTab: 'rpa-assistant', focusTaskId: task.id }, replace: true });
        try {
          await startRpaTaskRuntime({ userId, taskId: task.id });
        } catch {
        }
        loadTasks(false);
      } else {
        // console.log('🛑 [UI] Stopping RPA task:', { userId, taskId: task.id, title: task.project_title });
        await stopRpaTask({ userId, username, taskId: task.id, title: task.project_title });
        // console.log('🛑 [UI] Database stop completed, now calling runtime stop...');
        try {
          await stopRpaTaskRuntime({ userId, taskId: task.id });
          // console.log('✅ [UI] Runtime stop completed successfully');
        } catch (error) {
          console.error('❌ [UI] Runtime stop failed:', error);
          // Continue even if runtime stop fails
        }
        setRunningTaskId(null);

        // Update local tasks state immediately to show bot has stopped ('Y' is yet to start)
        setTasks((prevTasks) =>
          prevTasks.map((t) =>
            t.id === task.id ? { ...t, bot_status: 'Y' } : t
          )
        );

        // Show success popup immediately without waiting for table reload
        Swal.fire('Success', 'Bot stopped successfully', 'success');

        // Reload tasks in background without visual loading state
        loadTasks(false);
      }
    } catch (error: any) {
      if (action === 'start') {
        setRunningTaskId(null);
        setTasks((prevTasks) =>
          prevTasks.map((t) =>
            t.id === task.id ? { ...t, bot_status: 'Y' } : t
          )
        );
      }
      Swal.fire('Error', error.message || `Failed to ${action} bot`, 'error');
    }
  };

  const handleClientChange = async (clientId: string, clientName: string) => {
    setSelectedClientId(clientId);
    setSelectedClient(clientName);
    setSelectedTemplate('');
    setSelectedTemplateId('');
    setTemplateOptions([]);
    setTemplateCode([]);
    setTemplateHistoryCode([]);

    if (clientId) {
      try {
        const templates = await fetchTemplates(clientId);
        const mapped = (templates || []).map((t) => ({
          label: t.file_name,
          value: t.id,
        }));
        setTemplateOptions(mapped);
      } catch (err) {
        console.error('Failed to fetch templates:', err);
      }
    }
  };

  const handleTemplateChange = async (templateId: string, templateName: string) => {
    setSelectedTemplateId(templateId);
    setSelectedTemplate(templateName);

    if (!templateId) {
      setTemplateCode([]);
      setTemplateHistoryCode([]);
      return;
    }

    try {
      const result = await fetchTemplateCodes(templateId);
      setTemplateCode(result.codes);
      setTemplateHistoryCode(result.historyusages);
    } catch (err) {
      console.error('Failed to fetch template codes:', err);
      setTemplateCode([]);
      setTemplateHistoryCode([]);
    }
  };

  const openInstruction = async (task: BotManagerTask, mode: 'edit' | 'start' = 'edit') => {
    setSelectedTask(task);
    setInstructionMode(mode);
    setInstructionText(task.instruction || '');
    setInstructionTitle(task.project_title || '');
    setInstructionDomain(task.Domain || '');
    setInstructionDescription(task.Description || '');

    // Set client and template states from task data
    const cId = task.ClientId || '';
    const cName = task.ClientName || '';
    setSelectedClientId(cId);
    setSelectedClient(cName);

    const tId = task.templateId || '';
    const tName = task.templateName || task.template || '';
    setSelectedTemplateId(tId);
    setSelectedTemplate(tName);
    setTemplateOptions([]);
    setTemplateCode([]);
    setTemplateHistoryCode([]);
    if (cId) {
      try {
        const templates = await fetchTemplates(cId);
        const mapped = (templates || []).map((t) => ({
          label: t.file_name,
          value: t.id,
        }));
        setTemplateOptions(mapped);
      } catch (err) {
        console.error('Failed to load templates for client:', err);
      }
    }

    if (tId) {
      try {
        const result = await fetchTemplateCodes(tId);
        setTemplateCode(result.codes);
        setTemplateHistoryCode(result.historyusages);
      } catch (err) {
        console.error('Failed to fetch template codes:', err);
      }
    }

    const rawSteps = normalizeStepsToArray(task.steps).length > 0
      ? normalizeStepsToArray(task.steps).map((step: any, index: number) => ({
        step: Number(step?.step || index + 1),
        prompt: safeDisplay(step?.prompt || step?.description),
        action: safeDisplay(step?.action),
        tag: safeDisplay(step?.element?.tagname || step?.tag),
        id: safeDisplay(step?.element?.id || step?.id),
        class: safeDisplay(step?.element?.class || step?.className),
        xpath: safeDisplay(step?.element?.xpath || step?.xpath),
        input_type: safeDisplay(step?.element?.input_type || step?.input_type),
        placeholder: safeDisplay(step?.element?.placeholder || step?.placeholder),
      }))
      : [];
    const numberedSteps = numberDuplicatePlaceholders(rawSteps);
    const normalizedLoopSteps = normalizeLoopSteps(task.loop_steps);
    const detectedLoopCount = Math.max(
      1,
      getLoopCountFromSteps(normalizeStepsToArray(task.steps)),
      getLoopCountFromLoopSteps(normalizedLoopSteps)
    );
    const nextLoopSteps = {
      ...createLoopStepsMap(detectedLoopCount),
      ...normalizedLoopSteps,
    };
    const multipleSet = new Set(task.multiple || []);
    const initialMultipleChecked: Record<string, boolean> = {};
    const stepsArray = normalizeStepsToArray(task.steps);
    stepsArray.forEach((step: any, index: number) => {
      const prompt = step?.prompt || step?.description || '';
      const vars = extractPlaceholders(prompt);
      const isMultiple = vars.some((v) => multipleSet.has(v));
      if (isMultiple) {
        initialMultipleChecked[String(index + 1)] = true;
      }
    });

    setInstructionSteps(numberedSteps);
    setLoopSteps(nextLoopSteps);
    setLoopCount(detectedLoopCount);
    setCheckedItems({});
    setPendingSelectedValues({});
    setMultipleCheckedSteps(initialMultipleChecked);

    const extractedHeaders = extractDatasetHeaders(numberedSteps);
    const filteredHeaders = extractedHeaders.filter((h) => !multipleSet.has(h.key));
    const normalizedRows = normalizeDatasetRows(task.Dataset, filteredHeaders);
    setDatasetRows(normalizedRows);

    setInstructionOpen(true);
  };

  const selectedTaskSteps = useMemo(() => {
    return instructionSteps;
  }, [instructionSteps]);

  const datasetHeaders = useMemo(() => {
    const extractedHeaders = extractDatasetHeaders(instructionSteps);
    const multipleVars: string[] = [];
    instructionSteps.forEach((step, index) => {
      if (multipleCheckedSteps[String(index + 1)]) {
        const vars = extractPlaceholders(step.prompt || '');
        vars.forEach((v) => {
          if (!multipleVars.includes(v)) {
            multipleVars.push(v);
          }
        });
      }
    });
    const multipleSet = new Set(multipleVars);
    return extractedHeaders.filter((h) => !multipleSet.has(h.key));
  }, [instructionSteps, multipleCheckedSteps]);

  const hasPromptPlaceholder = (prompt: string) => /\{[^}]+\}/.test(prompt);

  const handleLoopCheckboxChange = (stepNum: number, isChecked: boolean) => {
    setCheckedItems((prev) => {
      const next = { ...prev };
      if (isChecked) {
        next[String(stepNum)] = true;
      } else {
        delete next[String(stepNum)];
      }
      return next;
    });
  };

  const handleMultipleCheckboxChange = (stepNum: number, isChecked: boolean) => {
    setMultipleCheckedSteps((prev) => {
      const next = { ...prev };
      if (isChecked) {
        next[String(stepNum)] = true;
      } else {
        delete next[String(stepNum)];
      }
      return next;
    });
  };

  const handlePendingValueChange = (stepNum: number, value: string) => {
    setPendingSelectedValues((prev) => ({
      ...prev,
      [String(stepNum)]: value,
    }));
  };

  const handleCreateLoop = () => {
    const selectedStepsList = Object.entries(checkedItems)
      .filter(([_, isChecked]) => isChecked)
      .map(([stepKey]) => Number(stepKey));

    if (selectedStepsList.length === 0) return;

    // Check if the selected steps are contiguous
    const sortedSteps = [...selectedStepsList].sort((a, b) => a - b);
    for (let i = 0; i < sortedSteps.length - 1; i++) {
      if (sortedSteps[i + 1] - sortedSteps[i] !== 1) {
        Swal.fire('Warning', 'Selected steps for a loop must be contiguous (consecutive with no missing steps in between).', 'warning');
        return;
      }
    }

    // Find the first available loop number based on active (non-empty) loops
    const activeLoopNumbers = new Set(
      Object.keys(loopSteps)
        .filter((k) => (loopSteps[k] || []).length > 0)
        .map((k) => getLoopNumber(k))
        .filter((n): n is number => n !== null)
    );
    let nextLoopNum = 1;
    while (activeLoopNumbers.has(nextLoopNum)) {
      nextLoopNum++;
    }
    const nextLoopKey = `loop_${nextLoopNum}`;

    // Build the new loop steps
    const newLoopStepsList = selectedStepsList.sort((a, b) => a - b).map((stepNum) => {
      const stepIndex = stepNum - 1;
      const hasDropdown = hasPromptPlaceholder(instructionSteps[stepIndex]?.prompt || '');
      return {
        step: stepKeyFromNumber(stepNum),
        value: hasDropdown ? (pendingSelectedValues[String(stepNum)] || 'select_input_data') : '',
      };
    });

    setLoopSteps((prev) => ({
      ...prev,
      [nextLoopKey]: newLoopStepsList,
    }));

    // Clear checkboxes and pending values
    setCheckedItems({});
    setPendingSelectedValues({});
  };

  const handleDeleteLoop = (loopKey: string) => {
    setLoopSteps((prev) => {
      const next = { ...prev };
      delete next[loopKey];
      return next;
    });
  };

  const handleRemoveStepFromLoop = (loopKey: string, stepNum: number) => {
    setLoopSteps((prev) => {
      const currentLoopSteps = prev[loopKey] || [];
      const updatedLoopSteps = currentLoopSteps.filter((item) => item.step !== stepKeyFromNumber(stepNum));

      const next = {
        ...prev,
        [loopKey]: updatedLoopSteps,
      };

      if (updatedLoopSteps.length === 0) {
        delete next[loopKey];
      }
      return next;
    });
  };

  const handleLoopStepValueChange = (loopKey: string, stepNum: number, value: string) => {
    setLoopSteps((prev) => {
      const currentLoopSteps = prev[loopKey] || [];
      const updatedLoopSteps = currentLoopSteps.map((item) => {
        if (item.step === stepKeyFromNumber(stepNum)) {
          return { ...item, value };
        }
        return item;
      });

      return {
        ...prev,
        [loopKey]: updatedLoopSteps,
      };
    });
  };

  const saveInstructionAndReturnSuccess = async (): Promise<boolean> => {
    if (!selectedTask) return false;

    // Check if any loop step in active loops has value 'select_input_data' or empty
    const hasUnselectedValue = Object.entries(loopSteps).some(([loopKey, steps]) => {
      if (!steps || steps.length === 0) return false;
      return steps.some((item) => {
        const stepNum = stepNumberFromKey(item.step);
        if (!stepNum) return false;
        const stepIndex = stepNum - 1;
        const stepPrompt = instructionSteps[stepIndex]?.prompt || '';
        const needsDropdown = hasPromptPlaceholder(stepPrompt);
        return needsDropdown && (!item.value || item.value === 'select_input_data');
      });
    });

    if (hasUnselectedValue) {
      Swal.fire('Warning', 'Please select a valid input data for all loop steps containing placeholders.', 'warning');
      return false;
    }

    try {
      const nextSteps = selectedTaskSteps.map((step, index) => {
        const originalSteps = normalizeStepsToArray(selectedTask.steps);
        const originalStep = originalSteps[index] || {};
        return {
          ...originalStep,
          step: index + 1,
          action: step.action,
          prompt: step.prompt,
          element: {
            // Preserve locators/fingerprint/shadowChain/parent_xpath/etc. —
            // only the fields the instruction editor actually lets you
            // change get overlaid. Replacing `element` wholesale here was
            // silently discarding everything RPA replay's smart-locator
            // resolution and generated-function matching depend on.
            ...(originalStep.element || {}),
            id: step.id,
            class: step.class,
            tagname: step.tag,
            xpath: step.xpath,
            input_type: step.input_type,
            placeholder: step.placeholder,
          },
        };
      });

      const numberedSteps = numberDuplicatePlaceholders(nextSteps);

      // Collect all variable names inside curly braces for checked steps
      const multipleVars: string[] = [];
      numberedSteps.forEach((step, index) => {
        if (multipleCheckedSteps[String(index + 1)]) {
          const vars = extractPlaceholders(step.prompt || '');
          vars.forEach((v) => {
            if (!multipleVars.includes(v)) {
              multipleVars.push(v);
            }
          });
        }
      });

      await updateRpaTask({
        id: selectedTask.id,
        project_title: instructionTitle,
        Domain: instructionDomain,
        Description: instructionDescription,
        steps: numberedSteps,
        dataset: datasetRows,
        UserId: userId,
        Username: username,
        loop_steps: loopSteps,
        multiple: multipleVars,
        Before: {
          project_title: selectedTask.project_title || '',
          Domain: selectedTask.Domain || '',
          Description: selectedTask.Description || '',
          steps: normalizeStepsToArray(selectedTask.steps),
          dataset: selectedTask.Dataset || [],
          loop_steps: selectedTask.loop_steps || [],
          multiple: selectedTask.multiple || [],
        },
        After: {
          project_title: instructionTitle,
          Domain: instructionDomain,
          Description: instructionDescription,
          steps: numberedSteps,
          dataset: datasetRows,
          loop_steps: loopSteps,
          multiple: multipleVars,
        },
      });

      setTasks((prevTasks) =>
        prevTasks.map((t) =>
          t.id === selectedTask.id
            ? {
              ...t,
              project_title: instructionTitle,
              Domain: instructionDomain,
              Description: instructionDescription,
              steps: numberedSteps,
              loop_steps: loopSteps,
              multiple: multipleVars,
              Dataset: datasetRows,
            }
            : t
        )
      );
      return true;
    } catch (error: any) {
      Swal.fire('Error', error.message || 'Failed to save instruction', 'error');
      return false;
    }
  };

  const handleSaveInstruction = async () => {
    const success = await saveInstructionAndReturnSuccess();
    if (success) {
      setInstructionOpen(false);
      Swal.fire('Success', 'Bot updated successfully', 'success');
    }
  };

  const handleSaveAndStart = async () => {
    if (!selectedTask) return;
    const success = await saveInstructionAndReturnSuccess();
    if (success) {
      setInstructionOpen(false);
      try {
        if ((window as any).taskMessagesCache) {
          (window as any).taskMessagesCache.delete(selectedTask.id);
        }
        setTasks((prevTasks) =>
          prevTasks.map((t) =>
            t.id === selectedTask.id ? { ...t, bot_status: 'I' } : t
          )
        );
        setRunningTaskId(selectedTask.id);
        await startRpaTask({ userId, username, rpaId: selectedTask.id, title: selectedTask.project_title });
        if (setActiveTab) {
          setActiveTab('rpa-assistant');
        }
        navigate('/bot-manager', { state: { activeTab: 'rpa-assistant', focusTaskId: selectedTask.id }, replace: true });

        startRpaTaskRuntime({ userId, taskId: selectedTask.id }).catch((runtimeError) => {
          console.warn('RPA Runtime start failed:', runtimeError);
        });

        Swal.fire('Success', 'Bot saved and started successfully!', 'success');
        loadTasks(false);
      } catch (error: any) {
        setRunningTaskId(null);
        setTasks((prevTasks) =>
          prevTasks.map((t) =>
            t.id === selectedTask.id ? { ...t, bot_status: 'Y' } : t
          )
        );
        Swal.fire('Error', error.message || 'Failed to start bot', 'error');
      }
    }
  };

  const handleAddStep = (index: number) => {
    const newStep = {
      step: index + 2,
      prompt: '',
      action: '',
      tag: '',
      id: '',
      class: '',
      xpath: '',
      input_type: '',
      placeholder: ''
    };
    setInstructionSteps((previous) => {
      const newSteps = [...previous];
      newSteps.splice(index + 1, 0, newStep);
      return newSteps.map((s, i) => ({ ...s, step: i + 1 }));
    });
    setLoopSteps((prev) => {
      const nextLoopSteps: LoopStepsMap = {};
      Object.entries(prev).forEach(([loopKey, steps]) => {
        nextLoopSteps[loopKey] = steps.map((item) => {
          const stepNum = stepNumberFromKey(item.step);
          if (stepNum && stepNum > index + 1) {
            return {
              ...item,
              step: stepKeyFromNumber(stepNum + 1),
            };
          }
          return item;
        });
      });
      return nextLoopSteps;
    });

    setCheckedItems((prev) => {
      const next: Record<string, boolean> = {};
      Object.entries(prev).forEach(([key, value]) => {
        const stepNum = Number(key);
        next[String(stepNum > index + 1 ? stepNum + 1 : stepNum)] = value;
      });
      return next;
    });
  };

  const handleDeleteStep = (index: number) => {
    setInstructionSteps((previous) => {
      const newSteps = previous.filter((_, i) => i !== index);
      return newSteps.map((s, i) => ({ ...s, step: i + 1 }));
    });
    setLoopSteps((prev) => {
      const nextLoopSteps: LoopStepsMap = {};
      Object.entries(prev).forEach(([loopKey, steps]) => {
        nextLoopSteps[loopKey] = steps
          .filter((item) => stepNumberFromKey(item.step) !== index + 1)
          .map((item) => {
            const stepNum = stepNumberFromKey(item.step);
            if (stepNum && stepNum > index + 1) {
              return {
                ...item,
                step: stepKeyFromNumber(stepNum - 1),
              };
            }
            return item;
          });
      });
      return nextLoopSteps;
    });

    setCheckedItems((prev) => {
      const next: Record<string, boolean> = {};
      Object.entries(prev).forEach(([key, value]) => {
        const stepNum = Number(key);
        if (stepNum === index + 1) return;
        next[String(stepNum > index + 1 ? stepNum - 1 : stepNum)] = value;
      });
      return next;
    });
  };

  const handleStepPromptChange = (index: number, value: string) => {
    setInstructionSteps((previous) => previous.map((step, currentIndex) => (
      currentIndex === index ? { ...step, prompt: value } : step
    )));
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSort((previous) => (previous === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSort('asc');
    }
  };

  const getSortIndicator = (column: string) => {
    if (sortColumn !== column) return <span className='ms-1 text-muted'>↕</span>;
    return <span className='ms-1'>{sort === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <Card className='border-0 shadow-sm'>
      <CardBody className='p-4'>
        <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3'>
          <div className='d-flex align-items-center gap-3'>
            <h4 className='mb-0 d-flex align-items-center gap-2'>
              <AiOutlineRobot size={24} />
              RPA Bot Management
            </h4>
            {/* {menuActions.includes('add') && (
              <Button
                color='primary'
                size='sm'
                className='d-flex align-items-center gap-2 rounded-pill px-3'
                onClick={() => navigate('/user-activity-stream')}
                style={{ fontSize: '12px', fontWeight: 600, height: '32px' }}
              >
                <Plus size={14} /> Create New RPA
              </Button>
            )} */}
          </div>
          <div className='d-flex flex-column flex-md-row align-items-md-center gap-3'>
            {userType === 'MOS' && (
              <div style={{ minWidth: 220 }}>
                <Label className='mb-1 small text-muted'>Select User</Label>
                <Select
                  options={users}
                  value={users.find((option) => option.value === filterUserId) || null}
                  onChange={(selectedOption: any) => setFilterUserId(selectedOption?.value || '')}
                  isSearchable
                  isClearable
                  placeholder='All Users'
                  styles={{
                    control: (styles) => ({
                      ...styles,
                      minHeight: '38px',
                      borderColor: '#cbd5e1',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#94a3b8'
                      }
                    }),
                    option: (styles, { isFocused, isSelected }) => ({
                      ...styles,
                      backgroundColor: isSelected ? '#3b82f6' : isFocused ? '#f1f5f9' : undefined,
                      color: isSelected ? '#ffffff' : '#1f2937'
                    })
                  }}
                />
              </div>
            )}
            <div style={{ minWidth: 180 }}>
              <Label className='mb-1 small text-muted'>Bot Status</Label>
              <Select
                options={BOT_STATUS_FILTER_OPTIONS}
                value={BOT_STATUS_FILTER_OPTIONS.find((option) => option.value === filterBotStatus) || null}
                onChange={(selectedOption: any) => setFilterBotStatus(selectedOption?.value || '')}
                isSearchable
                isClearable
                placeholder='All Status'
                styles={{
                  control: (styles) => ({
                    ...styles,
                    minHeight: '38px',
                    borderColor: '#cbd5e1',
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: '#94a3b8'
                    }
                  }),
                  option: (styles, { isFocused, isSelected }) => ({
                    ...styles,
                    backgroundColor: isSelected ? '#3b82f6' : isFocused ? '#f1f5f9' : undefined,
                    color: isSelected ? '#ffffff' : '#1f2937'
                  })
                }}
              />
            </div>
            <div className='align-self-md-end'>
              <ColumnChooser options={columnOptions} savedColumns={savedColumns} onChange={saveColumns} />
            </div>
            <div style={{ minWidth: 240 }}>
              <Label className='mb-1 small text-muted'>Search</Label>
              <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder='Search by Assistant...' />
            </div>
          </div>
        </div>

        <div className='table-responsive'>
          <Table borderless hover className='align-middle mb-0'>
            <thead style={{ background: 'linear-gradient(to right, #f5f3ff, #faf5ff)' }}>
              <tr className='text-uppercase small'>
                {isColVisible(columnOptions, savedColumns, 'BotName') && <th className='px-4 py-3' style={{ cursor: 'pointer' }} onClick={() => handleSort('project_title')}>Bot name {getSortIndicator('project_title')}</th>}
                {isColVisible(columnOptions, savedColumns, 'Description') && <th className='px-4 py-3' style={{ cursor: 'pointer' }} onClick={() => handleSort('Description')}>Description {getSortIndicator('Description')}</th>}
                {isColVisible(columnOptions, savedColumns, 'Username') && <th className='px-4 py-3' style={{ cursor: 'pointer' }} onClick={() => ('Username')}>Username </th>}
                {isColVisible(columnOptions, savedColumns, 'BotStatus') && <th className='px-4 py-3'>Bot Status</th>}
                {isColVisible(columnOptions, savedColumns, 'Actions') && <th className='px-4 py-3 text-center'>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={visibleColCount(columnOptions, savedColumns)} className='text-center py-4'><Spinner size='sm' /> Loading...</td></tr>
              ) : tasks.length === 0 ? (
                <tr><td colSpan={visibleColCount(columnOptions, savedColumns)} className='text-center py-4'>No Assistant Data</td></tr>
              ) : tasks.map((task) => (
                <tr key={task.id} className='border-bottom'>
                  {isColVisible(columnOptions, savedColumns, 'BotName') && <td className='px-4 py-3 fw-medium'>{task.project_title}</td>}
                  {isColVisible(columnOptions, savedColumns, 'Description') && <td className='px-4 py-3'>{task.Description || '—'}</td>}
                  {isColVisible(columnOptions, savedColumns, 'Username') && <td className='px-4 py-3'>{task.Username || '—'}</td>}
                  {isColVisible(columnOptions, savedColumns, 'BotStatus') && <td className='px-4 py-3'>
                    {task.bot_status === 'Y' ? (
                      <span className='badge bg-light-primary text-primary'>Yet to Start</span>
                    ) : task.bot_status === 'I' ? (
                      <span className='badge bg-light-success text-success'>In Progress</span>
                    ) : task.bot_status === 'S' ? (
                      <span className='badge bg-light-danger text-danger'>Stopped</span>
                    ) : task.bot_status === 'E' ? (
                      <span className='badge bg-light-danger text-danger'>Error</span>
                    ) : (
                      <span className='text-muted'>{task.bot_status || '—'}</span>
                    )}
                  </td>}
                  {isColVisible(columnOptions, savedColumns, 'Actions') && <td className='px-4 py-3 text-center'>
                    <div className='d-flex align-items-center justify-content-start flex-nowrap gap-3 mx-auto' style={{ whiteSpace: 'nowrap', width: 'fit-content', minWidth: '220px' }}>
                      {task.bot_status !== 'I' ? (
                        <button
                          id={`start-rpa-bot-${task.id}`}
                          type='button'
                          title={(task.RPA_status === 'C' || task.RPA_status === 'Y') && (task.bot_status === 'Y' || task.bot_status === 'E') ? 'Start RPA Bot' : 'RPA Bot not available'}
                          onClick={() => openInstruction(task, 'start')}
                          disabled={!((task.RPA_status === 'C' || task.RPA_status === 'Y') && (task.bot_status === 'Y' || task.bot_status === 'E'))}
                          className={`btn btn-link p-1 border-0 shadow-none ${(task.RPA_status === 'C' || task.RPA_status === 'Y') && (task.bot_status === 'Y' || task.bot_status === 'E') ? 'text-success' : 'text-muted opacity-50 cursor-not-allowed'}`}
                        >
                          <Play size={18} />
                        </button>
                      ) : (
                        <button
                          id={`stop-rpa-bot-${task.id}`}
                          type='button'
                          title='Stop RPA Bot'
                          onClick={() => handleStartStop(task, 'stop')}
                          className='btn btn-link p-1 border-0 shadow-none text-danger'
                        >
                          <RiStopCircleLine size={18} />
                        </button>
                      )}
                      {menuActions.includes('edit') && (
                        <button
                          id={`edit-bot-${task.id}`}
                          type='button'
                          title='Edit Bot'
                          onClick={() => openInstruction(task)}
                          className='btn btn-link p-1 border-0 shadow-none text-primary'
                        >
                          <Edit size={18} />
                        </button>
                      )}

                      {menuActions.includes('delete') && (
                        <button
                          id={`delete-bot-${task.id}`}
                          type='button'
                          title='Delete Bot'
                          onClick={() => handleDelete(task)}
                          className='btn btn-link p-1 border-0 shadow-none text-danger'
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                      <button
                        id={`audit-log-${task.id}`}
                        type='button'
                        title='Audit Log'
                        onClick={() => handleAudit(task)}
                        className='btn btn-link p-1 border-0 shadow-none'
                        style={{ color: '#8b5cf6' }}
                      >
                        <Eye size={18} />
                      </button>
                      {task.bot_status === 'E' && task.Flow_error_details?.message && (
                        <button
                          id={`flow-error-${task.id}`}
                          type='button'
                          title={task.Flow_error_details.message}
                          onClick={() => handleFlowError(task)}
                          className='btn btn-link p-1 border-0 shadow-none text-warning'
                        >
                          <FileText size={18} />
                        </button>
                      )}
                    </div>
                  </td>}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-center mt-4 gap-3'>
          <div className='d-flex align-items-center gap-2'>
            <span>Show</span>
            <Input type='select' value={perPage} onChange={(event) => { setPerPage(Number(event.target.value)); setPage(1); }} style={{ width: 90 }}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </Input>
            <span>entries</span>
          </div>
          <div>Showing {Math.min((page - 1) * perPage + 1, total)} to {Math.min(page * perPage, total)} of {total} results</div>
          <div className='d-flex align-items-center gap-2'>
            <Button color='light' disabled={page <= 1} onClick={() => setPage((previous) => Math.max(1, previous - 1))}>Previous</Button>
            <span className='fw-semibold'>{page} / {totalPages}</span>
            <Button color='light' disabled={page >= totalPages} onClick={() => setPage((previous) => Math.min(totalPages, previous + 1))}>Next</Button>
          </div>
        </div>
      </CardBody>

      <Modal
        style={{ maxWidth: '1500px', maxHeight: '600px' }}
        isOpen={auditOpen}
        toggle={() => setAuditOpen(false)}
        className='modal-dialog-centered modal-lg'
      >
        <ModalHeader toggle={() => setAuditOpen(false)}>
          Audit Log for {selectedTask?.project_title}
        </ModalHeader>
        <ModalBody>
          <div style={{ maxHeight: '600px', overflow: 'auto' }}>
            {auditLogs.length > 0 ? (
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f2f2f2' }}>
                    <th className="audit-tableHeaderStyle px-3 py-2 text-start">Date & Time</th>
                    <th className="audit-tableHeaderStyle px-3 py-2 text-start">Action</th>
                    <th className="audit-tableHeaderStyle px-3 py-2 text-start">Data Set</th>
                    <th className="audit-tableHeaderStyle px-3 py-2 text-start">Changed By</th>
                  </tr>
                </thead>
                <tbody style={{ alignItems: 'center' }}>
                  {auditLogs
                    .sort((a, b) => new Date(b.Modified).getTime() - new Date(a.Modified).getTime())
                    .map((item, index) => {
                      const hasBeforeAfter = item.Action === 'EDIT' && Array.isArray(item.JsonData) &&
                        item.JsonData.some((d: any) => 'before' in d || 'after' in d);
                      return (
                        <tr key={index} className="border-bottom">
                          <td className="audit-tableDataStyle px-3 py-2">
                            {moment.utc(item.Modified).tz(moment.tz.guess()).format('MM/DD/YYYY hh:mm:ss A')}
                          </td>
                          <td className="audit-tableDataStyle px-3 py-2">
                            <span className="badge bg-light text-dark border">{item.Action}</span>
                          </td>
                          <td className="audit-tableDataStyle px-3 py-2">
                            {item.Action === 'VIEW' ? (
                              <span>{String(item.JsonData)}</span>
                            ) : item.Action === 'DELETE' ? (
                              <span className="text-danger">{String(item.JsonData)}</span>
                            ) : (
                              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f9fafb' }}>
                                    <th className="audit-tableHeaderStyle px-2 py-1 text-start small">Field</th>
                                    {hasBeforeAfter ? (
                                      <>
                                        <th className="audit-tableHeaderStyle px-2 py-1 text-start small">Before</th>
                                        <th className="audit-tableHeaderStyle px-2 py-1 text-start small">After</th>
                                      </>
                                    ) : (
                                      <th className="audit-tableHeaderStyle px-2 py-1 text-start small">Value</th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {Array.isArray(item.JsonData) ? (
                                    item.JsonData
                                      .sort((a, b) => (a.field || '').localeCompare(b.field || ''))
                                      .map((dataItem, dataIndex) => (
                                        <tr key={dataIndex}>
                                          <td className="audit-tableDataStyle px-2 py-1 small">{dataItem.field || dataItem.Field || '—'}</td>
                                          {hasBeforeAfter ? (
                                            <>
                                              <td className="audit-tableDataStyle px-2 py-1 small">{renderAuditValue(dataItem.before, dataItem.field || dataItem.Field)}</td>
                                              <td className="audit-tableDataStyle px-2 py-1 small">{renderAuditValue(dataItem.after, dataItem.field || dataItem.Field)}</td>
                                            </>
                                          ) : (
                                            <td className="audit-tableDataStyle px-2 py-1 small">{renderAuditValue(dataItem.value ?? dataItem.Value, dataItem.field || dataItem.Field)}</td>
                                          )}
                                        </tr>
                                      ))
                                  ) : (
                                    <tr>
                                      <td colSpan={hasBeforeAfter ? 3 : 2} className="audit-tableDataStyle px-2 py-1 small text-break">
                                        {typeof item.JsonData === 'object' ? JSON.stringify(item.JsonData, null, 2) : String(item.JsonData)}
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            )}
                          </td>
                          <td className="audit-tableDataStyle px-3 py-2">
                            <span className="fw-medium text-primary">{item.UserName}</span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-4 text-muted">No data available</div>
            )}
          </div>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={errorModalOpen}
        toggle={() => setErrorModalOpen(false)}
        className='modal-dialog-centered modal-lg'
      >
        <ModalHeader toggle={() => setErrorModalOpen(false)}>
          RPA Execution Error Details
        </ModalHeader>
        <ModalBody className='p-4'>
          {errorLoading ? (
            <div className='text-center py-5'>
              <Spinner color='primary' className='mb-2' />
              <div className='text-muted small'>Retrieving and decrypting error details from S3...</div>
            </div>
          ) : errorDetails ? (
            <div>
              <div className='mb-4 p-3 rounded shadow-sm' style={{ backgroundColor: '#fff5f5', borderLeft: '4px solid #e53e3e' }}>
                <h6 style={{ color: '#c53030', fontWeight: 'bold', marginBottom: '8px', fontSize: '15px' }}>Error Message:</h6>
                <p className='mb-0 text-dark fw-medium' style={{ fontSize: '14px', lineHeight: '1.5' }}>
                  {errorDetails.error_message || 'No error message details available.'}
                </p>
                {errorDetails.Error_date && (
                  <div className='mt-3 small text-muted pt-2 border-top' style={{ borderColor: 'rgba(229, 62, 62, 0.15)' }}>
                    <strong>Date & Time:</strong> {
                      typeof errorDetails.Error_date === 'object' && errorDetails.Error_date?.$date
                        ? moment.utc(errorDetails.Error_date.$date).tz(moment.tz.guess()).format('MM/DD/YYYY hh:mm:ss A')
                        : moment.utc(errorDetails.Error_date).tz(moment.tz.guess()).format('MM/DD/YYYY hh:mm:ss A')
                    }
                  </div>
                )}
              </div>

              {errorDetails.image_base64 ? (
                <div>
                  <h6 className='fw-bold mb-2 text-secondary' style={{ fontSize: '14px' }}>Error Screenshot:</h6>
                  <div className='border rounded p-2 bg-light text-center shadow-inner' style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <img
                      src={`data:image/png;base64,${errorDetails.image_base64}`}
                      alt="Error Screen"
                      className='img-fluid rounded'
                      style={{ maxWidth: '100%', height: 'auto', border: '1px solid #dee2e6' }}
                    />
                  </div>
                </div>
              ) : (
                <div className='text-center py-4 text-muted bg-light rounded border' style={{ fontSize: '13px' }}>
                  No error screenshot available.
                </div>
              )}
            </div>
          ) : (
            <div className='text-center py-4 text-muted' style={{ fontSize: '13px' }}>
              No error details loaded.
            </div>
          )}
        </ModalBody>
        <ModalFooter className='border-0 pt-0'>
          <Button color='light' className='px-4' onClick={() => setErrorModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>



      <Modal scrollable isOpen={instructionOpen} toggle={() => setInstructionOpen(false)} size='xl' centered contentClassName='border-0 shadow'>
        <ModalHeader toggle={() => setInstructionOpen(false)}>
          Generated Instructions
        </ModalHeader>
        <ModalBody>
          <div className='p-3' style={{ background: '#f8fafc', borderRadius: 12 }}>
            <div className='mx-auto' style={{ maxWidth: 1100 }}>
              <div className='mb-4'>
                <div className='mb-3'>
                  <Label className='fw-semibold small'>Bot Name <span className='text-danger'>*</span></Label>
                  <Input
                    disabled={true}
                    value={instructionTitle}
                    onChange={(event) => setInstructionTitle(event.target.value)}
                    style={{ background: '#ffffff', color: '#111827', border: '1px solid #e5e7eb', borderRadius: 8 }}
                  />
                </div>
                {/* Hidden metadata fields as per user request */}
              </div>

              <div className='mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2 fw-bold' style={{ fontSize: '14px', color: '#1f2937' }}>
                <div>Total steps : {selectedTaskSteps.length}</div>
              </div>

              {/* Loop Builder & Management Controls */}
              <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                  <div className="fw-semibold" style={{ fontSize: '13px', color: '#334155' }}>
                    Loop Builder: Check steps below and click "Create Loop"
                  </div>
                  <Button
                    color="primary"
                    size="sm"
                    disabled={Object.values(checkedItems).filter(Boolean).length === 0}
                    onClick={handleCreateLoop}
                    className="d-flex align-items-center gap-1 fw-semibold"
                    style={{ borderRadius: 6 }}
                  >
                    <Plus size={14} /> Create Loop ({Object.values(checkedItems).filter(Boolean).length} selected)
                  </Button>
                </div>

                {/* Active Loops list */}
                {Object.keys(loopSteps).filter(k => (loopSteps[k] || []).length > 0).length > 0 ? (
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <span className="text-muted small fw-semibold" style={{ fontSize: '11px' }}>Active Loops:</span>
                    {Object.keys(loopSteps)
                      .filter(k => (loopSteps[k] || []).length > 0)
                      .sort((a, b) => (getLoopNumber(a) || 0) - (getLoopNumber(b) || 0))
                      .map((loopKey) => {
                        const stepsInLoop = loopSteps[loopKey] || [];
                        const loopNum = getLoopNumber(loopKey) || 1;
                        const color = loopColors[(loopNum - 1) % loopColors.length];
                        const stepNumbersList = stepsInLoop
                          .map(item => stepNumberFromKey(item.step))
                          .filter((n): n is number => n !== null)
                          .sort((a, b) => a - b);

                        return (
                          <span
                            key={loopKey}
                            className="badge d-flex align-items-center gap-2"
                            style={{
                              backgroundColor: color.bg,
                              color: color.text,
                              fontSize: '11px',
                              padding: '5px 8px',
                              borderRadius: '6px',
                              border: `1px solid ${color.text}33`
                            }}
                          >
                            <span>
                              {color.emoji} {loopKey.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())} (Steps: {stepNumbersList.join(', ')})
                            </span>
                            <button
                              type="button"
                              onClick={() => handleDeleteLoop(loopKey)}
                              style={{
                                border: 'none',
                                background: 'none',
                                color: color.text,
                                padding: 0,
                                fontSize: '12px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                lineHeight: 1
                              }}
                              title={`Delete ${loopKey}`}
                            >
                              &times;
                            </button>
                          </span>
                        );
                      })}
                  </div>
                ) : (
                  <div className="text-muted small italic" style={{ fontSize: '11px' }}>
                    No active loops. Select steps and click "Create Loop" to add one.
                  </div>
                )}
              </div>

              {/* Dataset Values / Bulk Data Entry Section */}
              {datasetHeaders.length > 0 && (
                <div className='mb-4 p-3 rounded' style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }}>
                  <div className="fw-semibold mb-3" style={{ fontSize: '13px', color: '#334155' }}>
                    Dataset Values
                  </div>
                  <div className='table-responsive border rounded' style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <Table className='mb-0 align-middle' bordered>
                      <thead className='table-light'>
                        <tr>
                          {datasetHeaders.map((header) => (
                            <th
                              key={header.key}
                              style={{
                                minWidth: 180,
                                position: 'sticky',
                                top: 0,
                                zIndex: 5,
                                background: '#f8f9fa',
                                borderTop: 0
                              }}
                            >
                              <div>
                                <div className='fw-semibold text-capitalize text-truncate pe-4' title={header.key.replace(/_/g, ' ')}>
                                  {header.key.replace(/_/g, ' ')}
                                </div>
                                <div className='small text-primary text-truncate pe-4' style={{ opacity: 0.8 }} title={header.description}>
                                  ({header.description})
                                </div>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {datasetRows.map((row, rowIndex) => (
                          <tr key={`dataset-row-${rowIndex}`}>
                            {datasetHeaders.map((header) => (
                              <td key={`${rowIndex}-${header.key}`} className='p-2'>
                                <Input
                                  className='form-control-sm'
                                  style={{ borderRadius: '4px' }}
                                  value={row[header.key] || ''}
                                  onChange={(event) => {
                                    const val = event.target.value;
                                    setDatasetRows((prev) =>
                                      prev.map((r, i) => (i === rowIndex ? { ...r, [header.key]: val } : r))
                                    );
                                  }}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}

              <div className='d-flex flex-column align-items-stretch' style={{ gap: 0, paddingRight: '8px' }}>
                {selectedTaskSteps.map((step, index) => (
                  <div key={`${step.step}-${index}`}>
                    {index > 0 && (
                      <div className='my-3' style={{ borderTop: '1px dashed #d1d5db' }}></div>
                    )}

                    <div className='w-100'>
                      <div
                        className='p-3 rounded'
                        style={{
                          background: '#ffffff',
                          border: '1px solid #e5e7eb',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                          width: '100%'
                        }}
                      >
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                          <div className='d-flex align-items-center gap-2 flex-wrap'>
                            <span className='fw-bold' style={{ color: '#111827', fontSize: '14px' }}>Step {index + 1}</span>
                            {/* Loop Badges */}
                            {Object.keys(loopSteps)
                              .sort((a, b) => (getLoopNumber(a) || 0) - (getLoopNumber(b) || 0))
                              .map((loopKey) => {
                                const inLoop = loopSteps[loopKey]?.some(item => item.step === stepKeyFromNumber(index + 1));
                                if (!inLoop) return null;
                                const loopNum = getLoopNumber(loopKey) || 1;
                                const color = loopColors[(loopNum - 1) % loopColors.length];
                                return (
                                  <span
                                    key={loopKey}
                                    className="badge d-flex align-items-center gap-1"
                                    style={{
                                      backgroundColor: color.bg,
                                      color: color.text,
                                      fontSize: '11px',
                                      padding: '3px 6px',
                                      borderRadius: '4px',
                                      border: `1px solid ${color.text}22`
                                    }}
                                  >
                                    {color.emoji} {loopKey.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                  </span>
                                );
                              })}
                          </div>
                          <div className='d-flex align-items-center gap-3'>
                            {hasPromptPlaceholder(step.prompt) && (
                              <div className='d-flex align-items-center gap-2'>
                                <span className="text-muted small" style={{ fontSize: '11px' }}>Single Input:</span>
                                <input
                                  type='checkbox'
                                  checked={!!multipleCheckedSteps[String(index + 1)]}
                                  title='Fields selected here will not be displayed on the Bulk Upload screen when uploading patient data.'
                                  onChange={(e) => {
                                    handleMultipleCheckboxChange(index + 1, e.target.checked);
                                  }}
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    cursor: 'pointer',
                                    accentColor: '#10b981'
                                  }}
                                />
                              </div>
                            )}
                            <div className='d-flex align-items-center gap-2'>
                              <span className="text-muted small" style={{ fontSize: '11px' }}>Include in loop:</span>
                              <input
                                type='checkbox'
                                checked={!!checkedItems[String(index + 1)]}
                                title='Select the steps that should repeat for each Procedure Code during Bulk Upload. These steps will be executed once for every Procedure Code provided for a patient.'
                                onChange={(e) => {
                                  handleLoopCheckboxChange(index + 1, e.target.checked);
                                }}
                                style={{
                                  width: '16px',
                                  height: '16px',
                                  cursor: 'pointer',
                                  accentColor: '#3b82f6'
                                }}
                              />
                            </div>
                          </div>
                        </div>

                        <div className='flex-grow-1'>
                          <Label className='small fw-semibold text-muted mb-1'>Prompt</Label>
                          <Input
                            type='textarea'
                            disabled={true}
                            rows={3}
                            value={step.prompt}
                            onChange={(event) => handleStepPromptChange(index, event.target.value)}
                            style={{
                              borderRadius: 8,
                              border: '1px solid #d1d5db',
                              background: '#f3f4f6',
                              minHeight: '90px',
                              resize: 'vertical',
                              fontSize: '13px',
                              color: '#6b7280'
                            }}
                          />
                        </div>

                        {/* Mapped Values Configuration for active loops and pending loop */}
                        {(Object.keys(loopSteps)
                          .sort((a, b) => (getLoopNumber(a) || 0) - (getLoopNumber(b) || 0))
                          .some(loopKey => loopSteps[loopKey]?.some(item => item.step === stepKeyFromNumber(index + 1))) ||
                          (!!checkedItems[String(index + 1)] && hasPromptPlaceholder(step.prompt))) && (
                            <div className='mt-2 d-flex flex-column gap-2'>
                              {/* Render existing active loops */}
                              {Object.keys(loopSteps)
                                .sort((a, b) => (getLoopNumber(a) || 0) - (getLoopNumber(b) || 0))
                                .map((loopKey) => {
                                  const stepItem = loopSteps[loopKey]?.find(item => item.step === stepKeyFromNumber(index + 1));
                                  if (!stepItem) return null;

                                  const loopNum = getLoopNumber(loopKey) || 1;
                                  const color = loopColors[(loopNum - 1) % loopColors.length];
                                  const hasDropdownValue = hasPromptPlaceholder(step.prompt);

                                  return (
                                    <div key={loopKey} className='d-flex align-items-center justify-content-between flex-wrap gap-2 p-2 rounded bg-light bg-opacity-50' style={{ border: '1px dashed #cbd5e1' }}>
                                      <div className='d-flex align-items-center gap-2 flex-wrap'>
                                        <span className='badge d-flex align-items-center gap-1' style={{ backgroundColor: color.bg, color: color.text, fontSize: '11px', padding: '4px 8px' }}>
                                          <span>{color.emoji} {loopKey.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                                          <button
                                            type="button"
                                            onClick={() => handleRemoveStepFromLoop(loopKey, index + 1)}
                                            style={{
                                              border: 'none',
                                              background: 'none',
                                              color: color.text,
                                              padding: '0 2px',
                                              fontSize: '12px',
                                              cursor: 'pointer',
                                              lineHeight: 1
                                            }}
                                            title={`Remove from ${loopKey}`}
                                          >
                                            &times;
                                          </button>
                                        </span>
                                        {hasDropdownValue && (
                                          <div style={{ minWidth: '180px' }}>
                                            <Select
                                              options={dropdownOptions}
                                              value={
                                                dropdownOptions.find((option) => option.value === stepItem.value)
                                                || dropdownOptions[0]
                                              }
                                              onChange={(selectedOption: any) => {
                                                handleLoopStepValueChange(loopKey, index + 1, selectedOption?.value || 'select_input_data');
                                              }}
                                              isSearchable={false}
                                              styles={{
                                                control: (styles) => ({
                                                  ...styles,
                                                  minHeight: '26px',
                                                  height: '26px',
                                                  border: '1px solid #cbd5e1',
                                                  borderRadius: 4,
                                                  boxShadow: 'none',
                                                  '&:hover': {
                                                    borderColor: '#94a3b8'
                                                  }
                                                }),
                                                valueContainer: (styles) => ({
                                                  ...styles,
                                                  padding: '0 6px',
                                                  fontSize: '11px'
                                                }),
                                                indicatorsContainer: (styles) => ({
                                                  ...styles,
                                                  height: '24px',
                                                  padding: 0
                                                }),
                                                option: (styles, { isFocused, isSelected }) => ({
                                                  ...styles,
                                                  backgroundColor: isSelected ? '#3b82f6' : isFocused ? '#f1f5f9' : undefined,
                                                  color: isSelected ? '#ffffff' : '#1f2937',
                                                  fontSize: '11px',
                                                  padding: '4px 8px'
                                                }),
                                                singleValue: (styles) => ({
                                                  ...styles,
                                                  color: '#1f2937',
                                                  fontSize: '11px'
                                                })
                                              }}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}

                              {/* Render pending loop dropdown */}
                              {!!checkedItems[String(index + 1)] && hasPromptPlaceholder(step.prompt) && (
                                <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 p-2 rounded bg-light bg-opacity-50' style={{ border: '1px dashed #3b82f6' }}>
                                  <div className='d-flex align-items-center gap-2 flex-wrap'>
                                    <span className='badge d-flex align-items-center gap-1' style={{ backgroundColor: '#dbeafe', color: '#1d4ed8', fontSize: '11px', padding: '4px 8px' }}>
                                      <span>Pending Loop</span>
                                    </span>
                                    <div style={{ minWidth: '180px' }}>
                                      <Select
                                        options={dropdownOptions}
                                        value={
                                          dropdownOptions.find((option) => option.value === (pendingSelectedValues[String(index + 1)] || 'select_input_data'))
                                          || dropdownOptions[0]
                                        }
                                        onChange={(selectedOption: any) => {
                                          handlePendingValueChange(index + 1, selectedOption?.value || 'select_input_data');
                                        }}
                                        isSearchable={false}
                                        styles={{
                                          control: (styles) => ({
                                            ...styles,
                                            minHeight: '26px',
                                            height: '26px',
                                            border: '1px solid #3b82f6',
                                            borderRadius: 4,
                                            boxShadow: 'none',
                                            '&:hover': {
                                              borderColor: '#2563eb'
                                            }
                                          }),
                                          valueContainer: (styles) => ({
                                            ...styles,
                                            padding: '0 6px',
                                            fontSize: '11px'
                                          }),
                                          indicatorsContainer: (styles) => ({
                                            ...styles,
                                            height: '24px',
                                            padding: 0
                                          }),
                                          option: (styles, { isFocused, isSelected }) => ({
                                            ...styles,
                                            backgroundColor: isSelected ? '#3b82f6' : isFocused ? '#f1f5f9' : undefined,
                                            color: isSelected ? '#ffffff' : '#1f2937',
                                            fontSize: '11px',
                                            padding: '4px 8px'
                                          }),
                                          singleValue: (styles) => ({
                                            ...styles,
                                            color: '#1f2937',
                                            fontSize: '11px'
                                          })
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                ))}

                {selectedTaskSteps.length === 0 && (
                  <div className='text-center py-4 text-muted bg-white rounded' style={{ border: '1px solid #e5e7eb' }}>
                    No steps defined for this assistant.
                  </div>
                )}
              </div>
            </div>
          </div>
        </ModalBody>
        <div className='d-flex justify-content-end gap-2 p-3 pt-0' style={{ background: '#ffffff' }}>
          <Button color='light' style={{ minWidth: 110 }} onClick={() => setInstructionOpen(false)}>
            Close
          </Button>
          {instructionMode === 'start' ? (
            <Button color='success' style={{ minWidth: 150 }} onClick={handleSaveAndStart}>
              Start Bot
            </Button>
          ) : (
            <Button color='primary' style={{ minWidth: 150 }} onClick={handleSaveInstruction}>
              Save Instructions
            </Button>
          )}
        </div>
      </Modal>
    </Card>
  );
};

export default RpabotManager;
